'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { Popover } from '@base-ui/react/popover';
import cn from 'classnames';
import { CaretDownIcon, CaretUpIcon, XIcon } from '@phosphor-icons/react';
import { useTranslation } from 'next-i18next';

import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import useScrollLock from '@hooks/useScrollLock';

import styles from './DropDown.module.scss';

type DropDownContextValue = {
  isMobileLayout: boolean;
  /** Raise popover/backdrop above `Modal` (--zIndex-dialog); use when nested inside a dialog. */
  stackAboveDialog: boolean;
  /** Popover panel matches trigger width (uses `--anchor-width` from Base UI). */
  matchAnchorWidth: boolean;
  /** Popup always opens below the trigger; never flips above when space is tight. */
  disableSideFlip: boolean;
};

const DropDownContext = createContext<DropDownContextValue | null>(null);

function useDropDownContext() {
  const ctx = useContext(DropDownContext);
  if (!ctx) {
    throw new Error('DropDown components must be used within DropDown.Root');
  }
  return ctx;
}

export type DropDownRootProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Set true when rendered inside `Modal` to keep backdrop/popup above dialog layering. */
  stackAboveDialog?: boolean;
  /**
   * When true, popup width follows the trigger (CSS `var(--anchor-width)`), not the default
   * fixed desktop width.
   */
  matchAnchorWidth?: boolean;
  /**
   * When true, the popup always opens below the trigger instead of flipping above when
   * there is not enough space underneath.
   */
  disableSideFlip?: boolean;
};

function DropDownRoot({
  children,
  open,
  defaultOpen,
  onOpenChange,
  stackAboveDialog = false,
  matchAnchorWidth = false,
  disableSideFlip = false,
}: DropDownRootProps) {
  const isMobileLayout = useBreakpoint(Breakpoints.sm);

  // `Popover.Root` below is rendered `modal={isMobileLayout}`, but Base UI's
  // own scroll lock explicitly excludes touch-opened popovers — a real tap on
  // the trigger *is* that, so the page behind a mobile sheet kept scrolling.
  // Every current caller controls `open` directly, so it's reliably available
  // here without extra state.
  useScrollLock(isMobileLayout && !!open);

  return (
    <DropDownContext.Provider
      value={{
        isMobileLayout,
        stackAboveDialog,
        matchAnchorWidth,
        disableSideFlip,
      }}
    >
      <Popover.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={isMobileLayout}
      >
        {children}
      </Popover.Root>
    </DropDownContext.Provider>
  );
}

export type DropDownTriggerProps = {
  variant?: 'primary' | 'secondary';
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

function DropDownTrigger({
  variant = 'primary',
  children,
  className,
  disabled,
  id,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedBy,
}: DropDownTriggerProps) {
  const { isMobileLayout } = useDropDownContext();

  return (
    <Popover.Trigger
      disabled={disabled}
      id={id}
      aria-labelledby={ariaLabelledby}
      render={(props, state) => {
        const { className: propsClassName, ...triggerProps } = props;
        return (
          <button
            type="button"
            {...triggerProps}
            aria-describedby={ariaDescribedBy}
            className={cn(
              styles.trigger,
              variant === 'primary' && styles.triggerPrimary,
              variant === 'primary' &&
                isMobileLayout &&
                styles.triggerPrimaryMobile,
              variant === 'secondary' && styles.triggerSecondary,
              state.open && variant === 'primary' && styles.triggerPrimaryOpen,
              state.open &&
                variant === 'secondary' &&
                styles.triggerSecondaryOpen,
              propsClassName,
              className
            )}
          >
            <span className={styles.triggerLabel}>{children}</span>
            {state.open ? (
              <CaretUpIcon
                className={styles.caret}
                size={20}
                aria-hidden
                weight="regular"
              />
            ) : (
              <CaretDownIcon
                className={styles.caret}
                size={20}
                aria-hidden
                weight="regular"
              />
            )}
          </button>
        );
      }}
    />
  );
}

export type DropDownContentProps = {
  children: ReactNode;
  className?: string;
  /**
   * Hands the popup's own right padding over to the internal scroll region
   * (topped up further) instead of the default half-and-half reclaim. Set
   * this when `children` contains its own independently-scrollable list
   * (e.g. ScrollableRefinementList) that needs to reserve its own scrollbar
   * gutter — without it, that nested list has no safe padding left to
   * reclaim and its scrollbar renders clipped. See DropDown.module.scss's
   * `.popupWideScrollGutter`/`.scrollRegionWideGutter` for the full case.
   */
  wideScrollGutter?: boolean;
};

function DropDownContent({
  children,
  className,
  wideScrollGutter = false,
}: DropDownContentProps) {
  const {
    isMobileLayout,
    stackAboveDialog,
    matchAnchorWidth,
    disableSideFlip,
  } = useDropDownContext();
  const { t } = useTranslation('common');

  return (
    <Popover.Portal>
      {isMobileLayout && (
        <Popover.Backdrop
          className={cn(
            styles.backdrop,
            stackAboveDialog && styles.backdropAboveDialog
          )}
        />
      )}
      <Popover.Positioner
        side="bottom"
        collisionAvoidance={disableSideFlip ? { side: 'none' } : undefined}
        align={isMobileLayout ? 'center' : 'start'}
        sideOffset={isMobileLayout ? 0 : 4}
        className={cn(
          styles.positioner,
          isMobileLayout && !matchAnchorWidth && styles.positionerMobile,
          matchAnchorWidth && styles.positionerMatchAnchor,
          stackAboveDialog && styles.positionerAboveDialog
        )}
      >
        <Popover.Popup
          className={cn(
            styles.popup,
            isMobileLayout
              ? styles.popupMobile
              : matchAnchorWidth
              ? styles.popupMatchAnchorDesktop
              : styles.popupDesktop,
            wideScrollGutter && styles.popupWideScrollGutter,
            className
          )}
        >
          {isMobileLayout && (
            <div className={styles.mobileHeader}>
              <Popover.Close
                type="button"
                className={styles.closeButton}
                aria-label={t('close')}
              >
                <XIcon size={24} aria-hidden weight="regular" />
              </Popover.Close>
            </div>
          )}
          <div
            className={cn(
              styles.scrollRegion,
              wideScrollGutter && styles.scrollRegionWideGutter
            )}
          >
            {children}
          </div>
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  );
}

const DropDown = {
  Root: DropDownRoot,
  Trigger: DropDownTrigger,
  Content: DropDownContent,
} as const;

export default DropDown;

export { DropDownRoot, DropDownTrigger, DropDownContent };
